//================================================================
//  Page: NewInitiative
//================================================================

//  Purpose: Form for New Initiative or Technology

//================================================================

//Libraries
import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components
import UserSearch from '../../../Components/UserSearch/UserSearch';
import PTELookupRHF from '../../../Components/PTELookupRHF/PTELookupRHF';

//Functions
import GetCollection from '../../../Library/GetCollection';
import WriteDocument from '../../../Library/WriteDocument';
import CreateRequest from '../../../Library/CreateRequest';

//Images

//CSS

export default function NewInitiative({
    setPageStatus,
    setFormData,
}) {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    const { register, handleSubmit, setValue, clearErrors, setError, watch, formState: { errors } } = useForm();

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    //  Submit Handler
    const onSubmit = (data) => {

        //------------------------------------------------------
        //  Validation Checks
        //------------------------------------------------------

       let preventSubmit = false;

        // Check User Selected for All Required User Search Fields
        //  - Business Sponsor
        if (data?.businesssponsorgivenname === undefined || data?.businesssponsorgivenname === '') {
            preventSubmit = true;
            setError('businesssponsor', {
                type: 'required',
                message: 'This field is required.'
            })
        }

        if (data.businesssponsorsurname === undefined || data.businesssponsorsurname === '') {
            preventSubmit = true;
            setError('businesssponsor', {
                type: 'required',
                message: 'This field is required.'
            })
        }

        //  - Line Manager
        if (data?.linemanagergivenname === undefined || data?.linemanagergivenname === '') {
            preventSubmit = true;
            setError('linemanager', {
                type: 'required',
                message: 'This field is required.'
            })
        }

        if (data.linemanagersurname === undefined || data.linemanagersurname === '') {
            preventSubmit = true;
            setError('linemanager', {
                type: 'required',
                message: 'This field is required.'
            })
        }

        //  - Finance Approver
        if (data?.financeapprovergivenname === undefined || data?.financeapprovergivenname === '') {
            preventSubmit = true;
            setError('financeapprover', {
                type: 'required',
                message: 'This field is required.'
            })
        }

        if (data.financeapproversurname === undefined || data.financeapproversurname === '') {
            preventSubmit = true;
            setError('financeapprover', {
                type: 'required',
                message: 'This field is required.'
            })
        }

        // Check if ALL validations have passed
        if (preventSubmit === true) return;

        //------------------------------------------------------
        //  Write Document
        //------------------------------------------------------

        setPageStatus('pending');

        const getApproversPromise = GetCollection('approvers');
        const createRequestPromise = CreateRequest(2);

        Promise.all([getApproversPromise, createRequestPromise])
            .then(async (results) => {

                const approvers = results[0];
                const requestIds = results[1];

                const docPromises = [];

                // ----------------------------------------------------------
                // Mapping from primaryusers to LoA role
                // ----------------------------------------------------------
                
                const loaRoleMapping = {
                    'Investment Management': 'Digital IDC',
                    'Investments': 'Digital IDC',
                    'Development': 'Digital IDC',
                    'Construction': 'Digital IDC',
                    'Defence': 'Digital IDC',
                    'Functions': 'Digital Functions',
                    'Digital': 'Technology',
                };

                // ----------------------------------------------------------
                // Mapped LoA approver role for the user
                // ----------------------------------------------------------

                const loaRole = loaRoleMapping[data?.primaryusers];

                // ----------------------------------------------------------
                //  LoA approver with matching role
                // ----------------------------------------------------------

                const loaApproverObj = approvers?.find(
                    (obj) => obj.approvalbucket === 'LoA Approval' && obj.role === loaRole
                );

                //=================================================
                //  Write Document to Firestore (Digital Request)
                //=================================================

                const digitalRequest = {

                    // Shared Metadata (Mandatory Fields)
                    'requestid': requestIds[0],
                    'parentrequestid': requestIds[0],
                    'tasktype': 'Digital Request',
                    'taskname': 'New Initiative or Technology Request',
                    'taskdescription': data?.initiativename,
                    'applicationid': '',
                    'applicationname': data?.applicationname,
                    'stage': '',
                    'status': 'Initialised',
                    'assignmentgroup': 'Demand Management',
                    'requesttype': 'New Initiative or Technology',
                    'activityfeedvisible': true,
                    'approvalhubvisible': true,
                    'region': data?.region,
                    'cancellationreason': '',
                    'assignedto': {
                        'email': 'Unassigned',
                        'givenname': '',
                        'surname': ''
                    },
                    'assigneddate': null,
                    'createddate': new Date(),
                    'createdby': {
                        'email': getUser.emailaddress,
                        'givenname': getUser.givenname,
                        'surname': getUser.surname,
                    },
                    'lastmodifieddate': new Date(),
                    'lastmodifiedby': {
                        'email': getUser.emailaddress,
                        'givenname': getUser.givenname,
                        'surname': getUser.surname,
                    },
                    'activityfeed': [
                        {
                            'activitydate': new Date(),
                            'actionedby': {
                                'email': getUser.emailaddress,
                                'givenname': getUser.givenname,
                                'surname': getUser.surname,
                            },
                            'action': 'created',
                        }
                    ],

                    // Digital Request Task Fields      
                    'costs': [],
                    'initiativename': data?.initiativename,
                    'vendorwebsite': data?.vendorwebsite,
                    'problemstatement': data?.problemstatement,
                    'expectedoutcome': data?.expectedoutcome,
                    'businessimpact': data?.businessimpact,
                    'implementationtype': data?.implementationtype,
                    'nextstage': data?.nextstage,
                    'businessunit': data?.businessunit,
                    'primaryusers': data?.primaryusers,
                    'businesssponsor': {
                        'email': data?.businesssponsor,
                        'givenname': data?.businesssponsorgivenname,
                        'surname': data?.businesssponsorsurname,
                    },
                    'linemanager': {
                        'email': data?.linemanager,
                        'givenname': data?.linemanagergivenname,
                        'surname': data?.linemanagersurname,
                    },
                    'financemanager': {
                        'email': data?.financeapprover,
                        'givenname': data?.financeapprovergivenname,
                        'surname': data?.financeapproversurname,
                    },
                    'estimatedcost': data?.estimatedcost,
                    'budgetavailable': data?.budgetavailable,
                    'projectcode': data?.projectcode,
                    'tasknumber': data?.tasknumber,
                    'expendituretype': data?.expendituretype,
                    'projectcodename': data?.projectcodename,
                    'otherinformation': data?.otherinformation,

                    // Approvers
                    'initialbuapprover': {
                        'email': approvers?.find((obj) => obj.approvalbucket === 'Initial Approval' && obj.role === data?.primaryusers)?.approveremail,
                        'givenname': approvers?.find((obj) => obj.approvalbucket === 'Initial Approval' && obj.role === data?.primaryusers)?.approvergivenname,
                        'surname': approvers?.find((obj) => obj.approvalbucket === 'Initial Approval' && obj.role === data?.primaryusers)?.approversurname,
                    },
                    'finalbuapprover': {
                        'email': approvers?.find((obj) => obj.approvalbucket === 'Final Approval' && obj.role === data?.businessunit)?.approveremail,
                        'givenname': approvers?.find((obj) => obj.approvalbucket === 'Final Approval' && obj.role === data?.businessunit)?.approvergivenname,
                        'surname': approvers?.find((obj) => obj.approvalbucket === 'Final Approval' && obj.role === data?.businessunit)?.approversurname,
                    },
                    'loaapprover': {
                        'email': loaApproverObj?.approveremail,
                        'givenname': loaApproverObj?.approvergivenname,
                        'surname': loaApproverObj?.approversurname,
                    },
                    'cooapprover': {
                        'email': approvers?.find((obj) => obj.approvalbucket === 'COO Approval')?.approveremail,
                        'givenname': approvers?.find((obj) => obj.approvalbucket === 'COO Approval')?.approvergivenname,
                        'surname': approvers?.find((obj) => obj.approvalbucket === 'COO Approval')?.approversurname,
                    },

                    //Other Fields
                    'vendorriskratings': [],
                    'sentfinalapprovals': false,
                    'demandassessment': {
                        'initiativecategories': [],
                        'businessfunctiontechalignment': '',
                        'strategyalignmentcommentary': '',
                        'optionsanalysis': '',
                        'digitalrecommendation': '',
                        'attacheddocuments': [],
                        'links': [],
                        'comments': []
                    },
                    'publishtoinventory': {
                        'applicationname': '',
                        'applicationdescription': '',
                        'businessowner': {
                            'email': '',
                            'givenname': '',
                            'surname': ''        
                        },
                        'itowner': {
                            'email': '',
                            'givenname': '',
                            'surname': ''        
                        },
                    },

                };

                docPromises.push(
                    WriteDocument('requests', requestIds[0], digitalRequest, true),
                );

                //=======================================================
                //  Write Document to Firestore (Initial Approval)
                //=======================================================

                const initialApproval = {
                    'requestid': requestIds[1],
                    'parentrequestid': requestIds[0],
                    'tasktype': 'Initial Approval',
                    'taskname': 'New Initiative or Technology Request',
                    'taskdescription': data?.applicationname?.length === 0 ? `Initial Approval for ${data?.applicationname}` : `Initial Approval for ${data?.initiativename}`,
                    'approverrole': data?.primaryusers,
                    'applicationname': data?.applicationname,
                    'initiativename': data?.initiativename,
                    'requestowner': {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname
                    },
                    'stage': '',
                    'status': 'Initialised',
                    'assignmentgroup': 'Demand Management',
                    'requesttype': 'New Initiative or Technology',
                    'activityfeedvisible': true,
                    'approvalhubvisible': true,
                    'region': '',
                    'assignedto': {
                        'email': digitalRequest?.initialbuapprover?.email,
                        'givenname': digitalRequest?.initialbuapprover?.givenname,
                        'surname': digitalRequest?.initialbuapprover?.surname
                    },
                    'assigneddate': new Date(Date.now() + 1000),
                    'createddate': new Date(Date.now() + 1000),
                    'createdby': {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname
                    },
                    'lastmodifieddate': new Date(Date.now() + 1000),
                    'lastmodifiedby': {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname
                    },
                    'activityfeed': [
                        {
                            'activitydate': new Date(Date.now() + 1000),
                            'actionedby': {
                                'email': digitalRequest?.initialbuapprover?.email,
                                'givenname': digitalRequest?.initialbuapprover?.givenname,
                                'surname': digitalRequest?.initialbuapprover?.surname
                            },
                            'action': 'assigned',
                            'comments': '',
                        }
                    ]
                }

                docPromises.push(
                    WriteDocument('requests', requestIds[1], initialApproval, true),
                );

                // Write request to Firestore
                return Promise.all(docPromises)
                    .then(() => {

                        setPageStatus('success');
                        setFormData(digitalRequest);

                    })

            })
            .catch((err) => {

                console.log('error', err);

                setPageStatus('error-fatal');

            })

    }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------


    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='h-full flex flex-col gap-2'>

            {/* ============================ */}
            {/*          Questions           */}
            {/* ============================ */}

            <form className='flex flex-col gap-4' onSubmit={handleSubmit(onSubmit)}>

                {/*==================1. What is the name of the initiative / request? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 1. What is the name of the initiative / request? <span className='text-[#C4314B]'>*</span> </label>
                    <input
                        className={errors.initiativename ? ('Input-Field-Text-Error  md:w-[450px]') : ('Input-Field-Text  md:w-[450px]')}
                        {...register('initiativename', {
                            required: 'This field is required',
                            maxLength: 100,
                        })}
                        placeholder='Enter a name for this initiative or application'
                        type='text'
                        autoComplete='off'
                    ></input>
                    {
                        errors.initiativename && errors.initiativename.type === 'maxLength' ? 
                            <p className='font-medium text-sm text-[#DE0000] my-2'>Maximum limit of 100 characters exceeded.</p>
                        : 
                        errors.initiativename && 
                            <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.initiativename?.message}</p>
                    }
                </div>

                {/*==================2. What is the application (if known)? =================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 2. What is the application (if known)? </label>
                    <input
                        className={errors.applicationname ? ('Input-Field-Text-Error  md:w-[450px]') : ('Input-Field-Text  md:w-[450px]')}
                        {...register('applicationname')}
                        placeholder='Leave blank if unknown or not applicable'
                        type='text'
                        autoComplete='off'
                    ></input>

                    {errors.applicationname && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.applicationname?.message}</p>}
                </div>

                {/*==================3. What is the vendor's website (if known)? =================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 3. What is the vendor's website (if known)? </label>
                    <input
                        className={errors.vendorwebsite ? ('Input-Field-Text-Error  md:w-[450px]') : ('Input-Field-Text  md:w-[450px]')}
                        {...register('vendorwebsite')}
                        placeholder='Leave blank if unknown or not applicable'
                        type='text'
                        autoComplete='off'
                    ></input>

                    {errors.vendorwebsite && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.vendorwebsite?.message}</p>}
                </div>

                {/*==================4. What is the problem you are trying to solve? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 4. What is the problem you are trying to solve? <span className='text-[#C4314B]'>*</span> </label>
                    <textarea
                        className={errors.problemstatement ? ('Input-Field-TextArea-Error  md:w-1/2') : ('Input-Field-TextArea  md:w-1/2')}
                        {...register('problemstatement', {
                            required: 'This field is required',
                        })}
                        placeholder='Describe the problem'
                        autoComplete='off'
                    ></textarea>

                    {errors.problemstatement && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.problemstatement?.message}</p>}
                </div>

                {/*==================5. What is the outcome you are looking for? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 5. What is the outcome you are looking for? <span className='text-[#C4314B]'>*</span> </label>
                    <textarea
                        className={errors.expectedoutcome ? ('Input-Field-TextArea-Error  md:w-1/2') : ('Input-Field-TextArea  md:w-1/2')}
                        {...register('expectedoutcome', {
                            required: 'This field is required',
                        })}
                        placeholder='Describe what success would look like'
                        autoComplete='off'
                    ></textarea>

                    {errors.expectedoutcome && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.expectedoutcome?.message}</p>}
                </div>

                {/*==================6. What would be the impact if this didn't go ahead? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 6. What would be the impact if this didn't go ahead? <span className='text-[#C4314B]'>*</span> </label>
                    <textarea
                        className={errors.businessimpact ? ('Input-Field-TextArea-Error  md:w-1/2') : ('Input-Field-TextArea  md:w-1/2')}
                        {...register('businessimpact', {
                            required: 'This field is required',
                        })}
                        placeholder='Describe what would happen and any Business impact if this request was not approved'
                        autoComplete='off'
                    ></textarea>

                    {errors.businessimpact && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.businessimpact?.message}</p>}
                </div>

                {/*==================7. Is this a technology trial or a full implementation? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 7. Is this a technology trial or a full implementation? <span className='text-[#C4314B]'>*</span> </label>
                    <p className='italic font-light'>Note: Technology trials last no longer than 12 months. Interim reviews with the trial users take place at 3, 6 and 9 months. At the 9 month trial a decision must be made regarding the future of the application and  must be submitted via LoA approval processes. </p>
                    <select
                        className={errors.implementationtype ? ('Input-Field-Select-Error  md:w-1/2') : ('Input-Field-Select  md:w-1/2')}
                        {...register('implementationtype', {
                            required: 'This field is required',
                        })}
                    >
                        <option hidden value=''>Select</option>
                        <option value='Trial'> Trial </option>
                        <option value='Full Implementation'> Full Implementation </option>
                        <option value='TBC'> TBC </option>
                    </select>

                    {errors.implementationtype && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.implementationtype?.message}</p>}
                </div>

                {/*==================8. If this trial is successful, what would the next stage be? =================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 8. If this trial is successful, what would the next stage be? </label>
                    <textarea
                        className={errors.nextstage ? ('Input-Field-TextArea-Error  md:w-1/2') : ('Input-Field-TextArea  md:w-1/2')}
                        {...register('nextstage')}
                        placeholder='Leave blank if unknown or not applicable'
                        autoComplete='off'
                    ></textarea>

                    {errors.nextstage && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.nextstage?.message}</p>}
                </div>

                {/*==================9. Which business unit is driving this initiative? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 9. Which business unit is driving this initiative? <span className='text-[#C4314B]'>*</span> </label>
                    <select
                        className={errors.businessunit ? ('Input-Field-Select-Error  md:w-1/2') : ('Input-Field-Select  md:w-1/2')}
                        {...register('businessunit', {
                            required: 'This field is required',
                        })}
                    >
                        <option hidden value=''>Select one</option>
                        <option value='Investment Management'> Investment Management </option>
                        <option value='Investments'> Investments </option>
                        <option value='Development'> Development </option>
                        <option value='Construction'> Construction </option>
                        <option value='Defence'> Defence </option>
                        <option value='Corporate Affairs and Marketing'> Corporate Affairs and Marketing </option>
                        <option value='Corporate Real Estate'> Corporate Real Estate </option>
                        <option value='Digital'> Digital </option>
                        <option value='EHS'> EHS </option>
                        <option value='Finance'> Finance </option>
                        <option value='Legal'> Legal </option>
                        <option value='People and Culture'> People and Culture </option>
                        <option value='Risk and Insurance'> Risk and Insurance </option>
                        <option value='Sustainability'> Sustainability </option>
                        <option value='Tax'> Tax </option>
                        <option value='Treasury'> Treasury </option>
                    </select>
                    {errors.businessunit && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.businessunit?.message}</p>}
                </div>

                {/*==================10. Who are the primary end users of this initiative or application? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 10. Who are the primary end users of this initiative or application? <span className='text-[#C4314B]'>*</span> </label>
                    <select
                        className={errors.primaryusers ? ('Input-Field-Select-Error  md:w-1/2') : ('Input-Field-Select  md:w-1/2')}
                        {...register('primaryusers', {
                            required: 'This field is required',
                        })}
                    >
                        <option hidden value=''>Select one</option>
                        <option value='Investment Management'> Investment Management </option>
                        <option value='Investments'> Investments </option>
                        <option value='Development'> Development </option>
                        <option value='Construction'> Construction </option>
                        <option value='Defence'> Defence </option>
                        <option value='Digital'> Digital </option>
                        <option value='Functions'> Functions </option>
                    </select>

                    {errors.primaryusers && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.primaryusers?.message}</p>}
                </div>

                {/*==================11. Which region(s) does this relate to? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 11. Which region(s) does this relate to? <span className='text-[#C4314B]'>*</span> </label>
                    <p className='text-[15px] mb-1 text-gray-600'>To select more than one, press Ctrl and select your options.</p>
                    <select
                        className={errors.region ? ('Input-Field-Select-Error  md:w-1/2') : ('Input-Field-Select  md:w-1/2')}
                        {...register('region', {
                            required: 'This field is required',
                        })}
                        style={{ height: '100px' }}
                        multiple
                    >
                        <option hidden value=''>Select all that apply</option>
                        <option value='America'> America </option>
                        <option value='Australia'> Australia </option>
                        <option value='China'> China </option>
                        <option value='Italy'> Italy </option>
                        <option value='Japan'> Japan </option>
                        <option value='Malaysia'> Malaysia </option>
                        <option value='Singapore'> Singapore </option>
                        <option value='United Kingdom'> United Kingdom </option>
                    </select>

                    {errors.region && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.region?.message}</p>}
                </div>

                {/*==================12. Who is the Business Sponsor of this initiative? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 12. Who is the Business Sponsor of this initiative? <span className='text-[#C4314B]'>*</span> </label>
                    <div className='w-full md:w-[450px]'>
                        <UserSearch
                            dataField={'businesssponsor'}
                            required={true}
                            register={register}
                            clearErrors={clearErrors}
                            setError={setError}
                            setValue={setValue}
                            errors={errors.businesssponsor}
                        ></UserSearch>
                    </div>
                </div>

                {/*==================13. Who is your Line Manager? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 13. Who is your Line Manager? <span className='text-[#C4314B]'>*</span> </label>
                    <div className='w-full md:w-[450px]'>
                        <UserSearch
                            dataField={'linemanager'}
                            required={true}
                            register={register}
                            clearErrors={clearErrors}
                            setError={setError}
                            setValue={setValue}
                            errors={errors.linemanager}
                        ></UserSearch>
                    </div>
                </div>

                {/*==================14. Who will be the Finance Approver of this request? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 14. Who will be the Finance Approver of this request? <span className='text-[#C4314B]'>*</span> </label>
                    <p className='italic font-light'>Who will approve the cost onto your budget?</p>
                    <div className='w-full md:w-[450px]'>
                        <UserSearch
                            dataField={'financeapprover'}
                            required={true}
                            register={register}
                            clearErrors={clearErrors}
                            setError={setError}
                            setValue={setValue}
                            errors={errors.financeapprover}
                        ></UserSearch>
                    </div>
                </div>

                {/*==================15. What is the estimated cost of this request? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 15. What is the estimated cost of this request? <span className='text-[#C4314B]'>*</span> </label>
                    <p className='italic font-light'>Please enter your best guess or estimate in AUD. This value can be updated later.</p>
                    <input
                        className={errors.estimatedcost ? ('Input-Field-Text-Error  md:w-[450px]') : ('Input-Field-Text  md:w-[450px]')}
                        {...register('estimatedcost', {
                            required: 'This field is required',
                            pattern: {
                                value: /^\d+(\.\d{1,2})?$/,
                                message: 'Only numbers allowed (up to 2 decimal places)',
                            },
                        })}
                        placeholder='Enter value'
                        type='number'
                        step='0.01'
                        autoComplete='off'
                    ></input>

                    {errors.estimatedcost && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.estimatedcost?.message}</p>}
                </div>

                {/*==================16. Do you have budget for this request? *=================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 16. Do you have budget for this request? <span className='text-[#C4314B]'>*</span> </label>
                    <p className='italic font-light'>Do you have agreed budget available for this in your operating plan?</p>
                    <select
                        className={errors.budgetavailable ? ('Input-Field-Select-Error  md:w-1/2') : ('Input-Field-Select  md:w-1/2')}
                        {...register('budgetavailable', {
                            required: 'This field is required',
                        })}
                    >
                        <option hidden value=''>Select one</option>
                        <option value='Yes'> Yes </option>
                        <option value='No'> No </option>
                        <option value='Unknown'> Unknown </option>
                        <option value='N/A'> N/A </option>
                    </select>

                    {errors.budgetavailable && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.budgetavailable?.message}</p>}
                </div>

                {/*==================17. What is the cost code for this request? =================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 17. What is the cost code for this request?</label>
                    <p className='italic font-light'>Please enter the cost code for any recharges. You do not have to enter it now, but it may help to speed up the completion of your request.</p>

                    <PTELookupRHF
                        required={false}
                        register={register}
                        setValue={setValue}
                        errors={errors}
                        clearErrors={clearErrors}
                        disabled={false}
                        watch={watch}
                    ></PTELookupRHF>

                </div>


                {/*==================18. Is there anything else important we should know? =================*/}
                <div className='Form-Row'>
                    <label className='font-medium'> 18. Is there anything else important we should know?</label>
                    <p className='italic font-light'>e.g. Are there any time constraints or compliance/security issues? Is this a Client requirement? Will this replace an existing technology? </p>
                    <textarea
                        className={errors.otherinformation ? ('Input-Field-TextArea-Error md:w-1/2') : ('Input-Field-TextArea md:w-1/2')}
                        {...register('otherinformation', {
                        })}
                        placeholder='Leave blank if unknown or not applicable'
                        autoComplete='off'
                    ></textarea>

                    {errors.otherinformation && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.otherinformation?.message}</p>}
                </div>

                {/* ===================================== */}
                {/*  Submit Buttons                       */}
                {/* ===================================== */}

                <div>
                    <button style={{ marginTop: '30px' }} className='Primary-Button max-md:w-full' type='submit' value='submit'>Submit Request</button>
                </div>

            </form>

        </div>
    )
}